import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  TextFieldProps,
  Button,
  Paper,
  // ListSubheader,
  // Divider,
  // Box,
} from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { ADD_PROJECT } from 'constants/routes';
import { FocusEvent, useMemo, useState, KeyboardEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { useConfigEntryOptionsQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';

type PostMultiSelectCustomFieldProps = {
  autoFocus?: boolean;
  field: ConfigEntryType;
  TextFieldProps?: TextFieldProps;
  showAddProject?: boolean;
};

type OptionType = { label: string; value: string };

function PostMultiSelectCustomField({
  autoFocus,
  field,
  TextFieldProps,
  showAddProject,
}: PostMultiSelectCustomFieldProps) {
  const navigate = useNavigate();
  const { companyId } = useUserData();
  const { singularProjectsModuleName } = useBuildWiseModules();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const {
    control,
    formState: { isSubmitting },
    getValues,
    setValue,
  } = useFormContext();
  const selectedOptions: string[] = useWatch({
    control,
    name: field?.entryCode,
    defaultValue: [],
  });
  const defaultSelectedOptions = useMemo(() => {
    return selectedOptions?.map?.((item) => ({ label: item, value: item })) || [];
  }, []);
  const [fieldValue, setFieldValue] = useState<OptionType[]>(defaultSelectedOptions);
  const { activeUniqueOrderedOptions: options, isLoading } = useConfigEntryOptionsQuery({
    companyId,
    configEntry: field,
    defaultOptions: defaultSelectedOptions,
  });

  const handleSelect = (_: unknown, values: (string | OptionType)[]) => {
    let selectedValues: string[] = [];
    let fieldValues: OptionType[] = [];
    values.map((item) => {
      if (typeof item === 'string') {
        selectedValues.push(item);
        fieldValues.push({ label: item, value: item });
      } else {
        selectedValues.push(item?.value);
        fieldValues.push(item);
      }
    });
    setValue(field.entryCode, selectedValues, { shouldDirty: true, shouldValidate: true });
    setFieldValue(fieldValues);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (options?.length && selectedOptions.length) {
      setFieldValue(options.filter((item) => selectedOptions.includes(item.value)));
    } else {
      setFieldValue([]);
    }
  }, [options, selectedOptions]);

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState, field: { ref } }) => (
          <FormControl fullWidth>
            <Autocomplete
              id={field.entryCode}
              {...(field?.referenceObject?.freeText && {
                freeSolo: true,
                open,
                onOpen: (event) => {
                  const currentValue = (event.target as HTMLInputElement).value;
                  if (currentValue?.trim()) {
                    setOpen(true);
                  }
                },
                onClose: () => setOpen(false),
                onKeyDown: handleKeyDown,
              })}
              multiple
              limitTags={3}
              disableCloseOnSelect
              inputValue={inputValue}
              onInputChange={(_, value) => {
                setInputValue(value);
                if (!value && open && !!field?.referenceObject?.freeText) {
                  setOpen(false);
                }
              }}
              onChange={handleSelect}
              options={options}
              autoHighlight
              value={fieldValue}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText={isLoading ? 'Loading...' : 'No Data Found'}
              // DevNote: These changes are part of BWA-1093, which is deprioritized due to requirement gaps.
              // groupBy={(option) => option.groupEntryBy || ''}
              // renderGroup={(params) => (
              //   <li key={params.key}>
              //     {params.group.replaceAll('-', '').trim() && (
              //       <ListSubheader sx={{ top: -8 }}>{params.group}</ListSubheader>
              //     )}
              //     <Box
              //       component="ul"
              //       sx={{
              //         p: 0,
              //         '& .MuiAutocomplete-opiton': {
              //           pl: params.group.replaceAll('-', '').trim() ? 3 : 0,
              //         },
              //       }}
              //     >
              //       {params.children}
              //     </Box>
              //     <Divider className="autocomplete-grouping-divider" sx={{ mx: 2, my: 0.5 }} />
              //   </li>
              // )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus={autoFocus}
                  inputRef={ref}
                  placeholder={`${field.referenceObject?.placeHolder || 'Select'}`}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  {...TextFieldProps}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.label}
                    deleteIcon={<CloseLineIcon size={20} />}
                  />
                ))
              }
              onBlur={(event: FocusEvent) => {
                if (
                  event.relatedTarget?.id === 'add-project' &&
                  showAddProject &&
                  field.entryCode === 'projectId'
                ) {
                  event.stopPropagation();
                  navigate(ADD_PROJECT, {
                    state: {
                      articleFormState: getValues(),
                      projectName: inputValue,
                    },
                  });
                }
              }}
              {...(field.entryCode === 'projectId' &&
                showAddProject && {
                  PaperComponent: ({ children, ...rest }) => (
                    <Paper {...rest}>
                      {children}
                      <Button
                        id="add-project"
                        startIcon={<AddLineIcon size={20} />}
                        sx={(theme) => ({ color: theme.palette.custom.link, m: 1 })}
                      >
                        Add {singularProjectsModuleName}
                      </Button>
                    </Paper>
                  ),
                })}
              disabled={isSubmitting}
            />
          </FormControl>
        )}
        name={`${field.entryCode}`}
      />
    </>
  );
}

export default PostMultiSelectCustomField;
